import { Routes, Route } from 'react-router-dom'
import { DateRangeProvider } from './context/DateRangeContext'
import { NotificationProvider } from './context/NotificationContext'
import { LanguageProvider, useLanguage } from './context/LanguageContext'
import { useAuth } from './context/AuthContext'
import { getAuthConfig, type AuthConfigResult } from './services/authConfig'
import DatePickerPanel from './components/DatePickerPanel'
import NotificationPanel from './components/NotificationPanel'
import LanguageSelector from './components/LanguageSelector'
import ManagerDashboard from './components/ManagerDashboard'
import UsersDetail from './pages/UsersDetail'
import UserManagement from './pages/UserManagement'
import LoginPage from './pages/LoginPage'

function LoadingSpinner() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-gray-400">Loading...</div>
    </div>
  )
}

function CenteredCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-lg p-10 max-w-md w-full text-center">
        {children}
      </div>
    </div>
  )
}

/** Apex host (gum.nextnovate.com): static signpost, no login. */
function ApexLandingPage() {
  const { t } = useLanguage()
  const baseDomain = getAuthConfig()?.base_domain ?? ''
  return (
    <CenteredCard>
      <h1 className="text-2xl font-bold text-gray-900 mb-3">{t.auth.apexTitle}</h1>
      <p className="text-gray-600">{t.auth.apexHint}</p>
      {baseDomain && (
        <p className="text-gray-400 text-sm mt-4 font-mono">&lt;org&gt;.{baseDomain}</p>
      )}
    </CenteredCard>
  )
}

/** 404: the subdomain maps to no known tenant. */
function UnknownTenantScreen() {
  const { t } = useLanguage()
  return (
    <CenteredCard>
      <p className="text-gray-700">{t.auth.unknownTenant}</p>
    </CenteredCard>
  )
}

/** 409: the tenant IdP is still being provisioned. */
function InitialisingScreen() {
  const { t } = useLanguage()
  return (
    <CenteredCard>
      <p className="text-gray-700">{t.auth.initialising}</p>
    </CenteredCard>
  )
}

/** 503 / bootstrap failure: sign-in temporarily unavailable. */
function AuthUnavailableScreen() {
  const { t } = useLanguage()
  return (
    <CenteredCard>
      <p className="text-gray-700">{t.auth.authUnavailable}</p>
    </CenteredCard>
  )
}

/** Authenticated but the account is not authorized for this tenant. */
function AccessDeniedScreen() {
  const { t } = useLanguage()
  const { email, logout } = useAuth()
  return (
    <CenteredCard>
      <h1 className="text-xl font-semibold text-gray-900 mb-2">{t.auth.accessDenied}</h1>
      <p className="text-gray-600 mb-2">{t.auth.accessDeniedHint}</p>
      {email && <p className="text-gray-400 text-sm mb-6 font-mono">{email}</p>}
      <button
        type="button"
        onClick={logout}
        className="rounded-md border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-800 shadow-sm transition hover:bg-gray-50"
      >
        {t.auth.signOut}
      </button>
    </CenteredCard>
  )
}

function Dashboard() {
  return (
    <NotificationProvider>
      <DateRangeProvider>
        <Routes>
          <Route path="/" element={<ManagerDashboard />} />
          <Route path="/users" element={<UsersDetail />} />
          {/* Admin-only; UserManagement itself re-checks the role. */}
          <Route path="/settings/users" element={<UserManagement />} />
        </Routes>
        <DatePickerPanel />
        <NotificationPanel />
        <LanguageSelector />
      </DateRangeProvider>
    </NotificationProvider>
  )
}

/** Firebase auth gate for a resolved tenant host. */
function TenantGate() {
  const { status } = useAuth()
  if (status === 'loading') return <LoadingSpinner />
  if (status === 'denied') return <AccessDeniedScreen />
  if (status === 'authenticated') return <Dashboard />
  return <LoginPage />
}

function AppBody({ result }: { result: AuthConfigResult }) {
  if (result.status === 'not_found') return <UnknownTenantScreen />
  if (result.status === 'initialising') return <InitialisingScreen />
  if (result.status === 'error') return <AuthUnavailableScreen />

  const config = result.config
  if (!config.enabled) return <Dashboard /> // dev / no-auth
  if (!config.tenant) return <ApexLandingPage /> // apex host
  return <TenantGate /> // resolved tenant host
}

export default function App({ result }: { result: AuthConfigResult }) {
  return (
    <LanguageProvider>
      <AppBody result={result} />
    </LanguageProvider>
  )
}
